import React, { useState, useCallback, useContext, useEffect } from 'react';
import _ from 'lodash'
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { Loader } from '@jutro/components';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import wizardStyles from 'twia-portals-wizard-components-ui/common/TWIAWizard.module.scss'
import { PropertyCodeLookupService, CpCoverablesService } from 'twia-capability-quoteandbind-cp'
import { BreakpointTrackerContext } from '@jutro/layout';
import TWIAJobHelper from 'ent-capability-quoteandbind-common-react/util/TWIAJobHelper';
import metadata from './BuildingStep.metadata.json5';
import TWIABuildingLocationAccordion from '../../TWIABuildingSummaryAccordions/TWIABuildingLocationAccordion/TWIABuildingLocationAccordion';
import { BldgLocContext } from '../../../context/BuildingsAndLocationContext';
import TWIACleanseObjUtil from '../../../util/TWIACleanseObjUtil';
import { TranslatorContext } from '@jutro/locale';
import messages from './BuildingStep.messages';
import TWIAWizardMessagesMessages from '../../../../../modules-react/twia-portals-wizard-components-ui/common/TWIAWizardMessages.messages';
import TWIACPBuildingLocationsHelper from '../../../util/TWIACPBuildingLocationsHelper';

function BuildingStep(props) {
    const { id, submissionVM, handleNextClick, handleCancelClick, handleEditLinkClick, showStepper, updateWizardData } = props;
    const breakpoint = useContext(BreakpointTrackerContext);
    //  US26272
    const translator = useContext(TranslatorContext);
    const { isComponentValid, onValidate } = useValidation(id);
    const [showErrors, setShowErrors] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const { chosenLocation, chosenBuildingIndex } = useContext(BldgLocContext);
    const [selectedLocation, setSelectedLocation] = chosenLocation;
    const [selectedBuildingIndex, setSelectedBuildingIndex] = chosenBuildingIndex;
    const [buildingVM, setBuildingVM] = useState();
    const [coverageFormEditable, setCoverageFormEditable] = useState(false)
    const [propertyCodeValues, setPropertyCodeValues] = useState([])
    const viewModelService = useContext(ViewModelServiceContext)
    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();
    const locations = submissionVM.lobData.commercialProperty.coverables.locations.value
    const locationIndex = _.findIndex(locations, (loc) => loc.publicID === chosenLocation[0].publicID.value);

    const jobID = _.get(submissionVM, 'quoteID.value') || _.get(submissionVM, 'jobID.value');
    const sessionID = submissionVM.sessionUUID.value;
    let isExistingBuilding = !_.isUndefined(selectedBuildingIndex);
    const isAddAnotherBuilding = _.get(submissionVM, 'isAddBuilding') // DE16312
    const isPolicyChangeOrRenewal = TWIAJobHelper.isEitherRenewalOrPolicyChange(submissionVM.value);

    // Returns default Risk Item Type based on Coverage Form
    const getRiskItemTypeDefault = (coverageFormCode) => ['TWCP', 'TWCB'].includes(coverageFormCode) ? 'CommBldgOrBPP' : undefined;

    useEffect(() => {
        setLoading(true);

        // US26385
        const locations = _.get(submissionVM, 'value.lobData.commercialProperty.coverables.locations');
        
        const shouldSelectBuilding = isExistingBuilding && (!isAddAnotherBuilding); // DE18875

        const initialBuilding = shouldSelectBuilding ? selectedLocation.buildings.value[selectedBuildingIndex] : { classCode: undefined };
        const building = _.extend({}, initialBuilding)
        const buildingView = viewModelService.create(building, 'pc', 'edge.capabilities.policyjob.lob.commercialproperty.coverables.dto.BuildingDTO');
        const allBuildings = locations.reduce((a, b) => a.concat(b.buildings), []);
        const constructionAspects = _.get(buildingView, 'construction_TWIA.aspects');
        const coverageForm = _.get(initialBuilding, 'coverageForm') || selectedLocation.buildings?.value[0]?.coverageForm
        
        if (allBuildings.length >= 1) {
            const covForm = _.sortBy(allBuildings, ['buildingNumber'])[0].coverageForm;
            buildingView.coverageForm = viewModelService.productMetadata.get('pc').types.getTypelist('CoverageForm').getCode(covForm);

            loadInitialClasscodes(buildingView)
        }

        isExistingBuilding = buildingView.value.publicID !== undefined;
        if (allBuildings.length === 0) {
            setCoverageFormEditable(true);
            if (isExistingBuilding) {
                loadInitialClasscodes(buildingView)
            }
        }

        // DE15982
        if (coverageForm && _.isUndefined(selectedBuildingIndex)) {         
            _.set(buildingView, 'riskItemType_TWIA', getRiskItemTypeDefault(coverageForm))
            setCoverageFormEditable(false);
        }

        buildingView.rateType = 'Class';

        if (constructionAspects && !constructionAspects.subtreeValid) {
            _.set(buildingView, 'construction_TWIA.value', null)
        }

        // US26440 // DE17380
        if(isPolicyChangeOrRenewal){
            _.set(buildingView,'coverageForm.value',submissionVM.baseData.originalCoverageForm.value)
            loadInitialClasscodes(buildingView)
        }
        
        setBuildingVM(buildingView);
        setLoading(false);
    }, [])

    const loadInitialClasscodes = async (buildingVM) => {
        const propertyClassCodeValues = await PropertyCodeLookupService.getPropertyCodes(
            'CommercialProperty',
            jobID,
            buildingVM.classCode.value || null,
            selectedLocation.address.value,
            sessionID,
            buildingVM?.coverageForm?.value?.code,
            authHeader
        );

        const propertyClassCodeArray = propertyClassCodeValues.map(classCode => ({
            name: `${classCode?.code}: ${classCode?.classification}`,
            code: classCode?.publicID,
            codeValue: classCode?.code,
            classification: classCode.classification
        }))

        setPropertyCodeValues(propertyClassCodeArray)
        handleModelChange(buildingVM)
    }

    
    // DE15974
    const classCodeChange = (value, path) => {
        
        const propertyCode = propertyCodeValues.find(classCode => classCode.code === value)
        const propertyCodeObject = {
            code: propertyCode.codeValue,
            publicID: propertyCode.code,
            classification: propertyCode.classification
        }

        _.set(buildingVM, `${path}.value`, propertyCodeObject)
        handleModelChange(buildingVM)
    }

    // DE15820 and DE15832
    const riskItemChange = (value, path) => {
        _.set(buildingVM, `${path}.value`, value)
        _.set(buildingVM, 'construction_TWIA.value', null)
        handleModelChange(buildingVM)
    }

    const coverageFormChange = async (value, path) => {
        _.set(buildingVM, `${path}.value`, value)
        _.set(buildingVM, `classCode.value`, undefined)
        // Set default Risk Item Type on new buildings based on Coverage Form
        _.set(buildingVM, 'riskItemType_TWIA', getRiskItemTypeDefault(value))
        _.set(buildingVM, `buildersRiskForm_TWIA.value`, undefined)

        const propertyClassCodeValues = await PropertyCodeLookupService.getPropertyCodes(
            'CommercialProperty',
            jobID,
            buildingVM.classCode.value || null,
            selectedLocation.address.value,
            sessionID,
            buildingVM.coverageForm.value.code,
            authHeader
        );

        const propertyClassCodeArray = propertyClassCodeValues.map(classCode => ({
            name: `${classCode.code}: ${classCode.classification}`,
            code: classCode.publicID,
            codeValue: classCode.code,
            classification: classCode.classification
        }))
      
        setPropertyCodeValues(propertyClassCodeArray)
        handleModelChange(buildingVM)
    }


    function validateAndSubstituteNumericInput(viewModel) {
        ['yearBuilt', 'numberOfStories', 'totalAreaExcludingBasement'].forEach(key => {
            if (!viewModel[key]) {
                viewModel[key] = null;
            }
        });
        return viewModel;
    }

    const getBuildingToSave = () => {
        // TWIACleanseObjUtil.resetAspectsNotVisibleValues(buildingVM)

        const newBuilding = validateAndSubstituteNumericInput(buildingVM.value);
        newBuilding.displayName = `${newBuilding.description}`;
        return newBuilding;
    };

    const onNext = async () => {
        if (!isComponentValid) {
            setShowErrors(true);
            return false
        }

        const building = getBuildingToSave();
        const shouldUpdate = isExistingBuilding && building.publicID !== undefined && 
        (isPolicyChangeOrRenewal || !isAddAnotherBuilding); // DE17176

        const rpcCall = shouldUpdate ? CpCoverablesService.updateCPBuilding : CpCoverablesService.addCPBuilding;
     
        try {
            const result = await rpcCall(jobID, selectedLocation.publicID.value, building, sessionID, authHeader)
            _.extend(building, result);
            const selectedLocationFromSubmissionVM = submissionVM.lobData.commercialProperty.coverables.locations.value.find((locn) => locn.publicID == selectedLocation.publicID.value);
            if (selectedLocationFromSubmissionVM.buildings.length > 0 &&
                selectedLocation.buildings.value.length === 0
            ) {
                _.each(selectedLocationFromSubmissionVM.buildings, (bldg) => {
                    selectedLocation.buildings.value.push(bldg);
                })

            }
            const buildings = selectedLocation.buildings.value

            _.remove(buildings, (currentBuilding) => {
                return currentBuilding.publicID === building.publicID;
            });

            const buildingIndex = _.findIndex(selectedLocation.buildings.value, (blding) => {
                return blding.publicID === building.publicID;
            });

            if (buildingIndex >= 0) {
                selectedLocation.buildings.value.splice(buildingIndex, 1, building);
                setSelectedBuildingIndex(buildingIndex)
                _.set(submissionVM, `lobData.commercialProperty.coverables.locations.value[${locationIndex}].buildings[${buildingIndex}]`, result);
                submissionVM.selectedBuilding = building
                submissionVM.setSelectedBuildingIndex = setSelectedBuildingIndex

            } else {
                selectedLocation.buildings.value.push(building);
                const buildingIndex = _.findIndex(selectedLocation.buildings.value, (blding) => {
                    return blding.publicID === building.publicID;
                });
                _.set(submissionVM, `lobData.commercialProperty.coverables.locations.value[${locationIndex}].buildings[${buildingIndex}]`, result);
                setSelectedBuildingIndex(buildingIndex)
                submissionVM.selectedBuilding = building
                submissionVM.setSelectedBuildingIndex = setSelectedBuildingIndex

            }
            updateWizardData(submissionVM);
            setSelectedLocation(selectedLocation);


            handleNextClick();

        }
        catch (e) {
            console.log(e)
        }


    }

    const handleModelChange = useCallback(
        newModel => {
            setBuildingVM(newModel);
            refreshData();
        },
        [refreshData]
    );

    const cancelBuilding = useCallback(async () => {
        await handleCancelClick(selectedLocation, setSelectedLocation);
    }, [handleCancelClick, selectedLocation, setSelectedLocation]);

    // US26440
    const readOnlyFields = useCallback(()=>{
        const isNewOnCurrentTermUndefined = _.isUndefined(_.get(buildingVM, 'isNewOnCurrentTerm_TWIA.value'));
        const coverageFormCode = _.get(buildingVM, 'coverageForm.value.code');
        const riskItemTypeUndefined = _.isUndefined(_.get(buildingVM, 'riskItemType_TWIA.value'));

        if (isPolicyChangeOrRenewal && isNewOnCurrentTermUndefined 
            && !_.isUndefined(coverageFormCode)) {
            const riskItemType = riskItemTypeUndefined && _.isUndefined(selectedBuildingIndex)
                ? getRiskItemTypeDefault(coverageFormCode)
                : _.get(buildingVM, 'riskItemType_TWIA.value');
            
                _.set(buildingVM, 'riskItemType_TWIA', riskItemType);
            
                return true;
        }

       // DE17207
        return isPolicyChangeOrRenewal  && !showStepper
            ? true//_.get(buildingVM, 'isNewOnCurrentTerm_TWIA.value')
            : !coverageFormEditable;

    },[buildingVM, coverageFormEditable, isPolicyChangeOrRenewal, selectedBuildingIndex, showStepper])
    
    // US26387 US29240 US29242 US29242
    const isBuildingExistingOnTerm = useCallback(() => {
        if(!_.isUndefined(buildingVM)){
            const building = getBuildingToSave();

            return TWIACPBuildingLocationsHelper.isBuildingExistsOnBasedOn
            (_.get(building, 'publicID'), submissionVM.value)
              && !showStepper
        }

        return false;
    }, [buildingVM, getBuildingToSave, showStepper, submissionVM]);

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showRequired: true
        },
        locationAccordion: {
            visible: showStepper,
            handleEditLinkClick,
            location: selectedLocation.value
        },
        // US26440
        coverageForm: {
            readOnly: readOnlyFields(),
            onValueChange: coverageFormChange,
            validationMessages: buildingVM?.coverageForm?.aspects?.valid ? [] : [translator(messages.required)]
        },
        classCode: {
            readOnly: isBuildingExistingOnTerm(),
            availableValues: propertyCodeValues,
            onValueChange: classCodeChange,
            value: buildingVM?.classCode?.publicID?.value,
            validationMessages: buildingVM?.classCode?.aspects?.valid ? [] : [translator(messages.required)]
        },
        riskItemType: {
            readOnly: isBuildingExistingOnTerm(),
            onValueChange: riskItemChange, // DE15820 and DE15832
            validationMessages: buildingVM?.riskItemType_TWIA?.aspects?.valid ? [] : [translator(messages.required)]
        },
        buildersRiskForm: {
            readOnly: isBuildingExistingOnTerm(),
            validationMessages: buildingVM?.buildersRiskForm_TWIA?.aspects?.valid ? [] : [translator(messages.required)]
        },
        nextButton: {
            content: !showStepper ? translator(TWIAWizardMessagesMessages.TWIAWizardModalSave)
                : translator(TWIAWizardMessagesMessages.TWIAWizardModalNext)
        },
        wizardActionButtons: {
            justifyContent: !showStepper ? 'right' : 'between'
        }
    }


    const resolvers = {
        resolveClassNameMap: wizardStyles,
        resolveCallbackMap: {
            onNext,
            handleCancelClick: cancelBuilding
        },
        resolveComponentMap: {
            TWIABuildingLocationAccordion
        }
    }

    return (
        <Loader loaded={!isLoading}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrideProps}
                model={buildingVM}
                onModelChange={handleModelChange}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                showErrors={showErrors}
            />
        </Loader>
    )
}

export default BuildingStep